import { useState } from 'react'

import { PlusOutlined } from '@ant-design/icons'
import { Dropdown, MenuProps, Tag } from 'antd'
import styled from 'styled-components'

interface StatusSelectorProps {
  status?: {
    label: string
    key: string
    color: string
  }
  statuses: {
    label: string
    key: string
    color: string
  }[]
  onChangeStatus: (status: { label: string, key: string, color: string }) => void
  className?: string
  disabled?: boolean
  showBorder?: boolean
  defaultStatusConfig?: {
    borderColor: string
    label: string
    color: string
    backgroundColor: string
    fontSize: string
    padding: string
    height: string
  }
}

export const StatusSelector = ({ status, statuses, onChangeStatus, className, disabled, defaultStatusConfig, showBorder = true }: StatusSelectorProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const defaultStatus = {
    label: 'Add status',
    color: 'default',
    key: 'default'
  }

  const getStatusLabel = (status: { label: string, color: string }, isDefault?: boolean) => {
    return (
      <Tag
        color={status.color}
        bordered={isDefault || showBorder}
        style={{
          margin: 0,
          lineHeight: '22px',
          fontSize: (isDefault && defaultStatusConfig) ? defaultStatusConfig.fontSize : '14px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {isDefault && <PlusOutlined style={{ fontSize: '13px' }} />}

        {status.label}
      </Tag>
    )
  }

  const handleItemClick = (item: any) => {
    onChangeStatus(item)
    setIsMenuOpen(false)
  }

  const getStatusMenuProps = (): MenuProps => {
    return {
      items: statuses.map(item => ({
        key: item.key,
        label: getStatusLabel(item),
      })),
      onClick: (e) => {
        const item = statuses.find((item) => item.key === e.key)
        if (item) {
          handleItemClick(item)
        }
      },
    }
  }

  return (
    <SStatusSelector $showBorder={showBorder}>
      <Dropdown
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        menu={getStatusMenuProps()}
        trigger={['click']}
        className={className}
        disabled={disabled}
        dropdownRender={() => (
          <SMenuWrapper $showBorder={showBorder}>
            {statuses.map(item => item.key !== status?.key && (
              <STag
                color={item.color}
                style={{
                  margin: 0,
                  padding: '0 12px',
                }}
                bordered={showBorder}
                key={item.key}
                onClick={() => handleItemClick(item)}
              >
                {item.label}
              </STag>
            ))}
          </SMenuWrapper>
        )}
      >
        {status ? getStatusLabel(status, false) : getStatusLabel(defaultStatus, true)}
      </Dropdown>
    </SStatusSelector>
  )
}

const SStatusSelector = styled.div<{ $showBorder: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;

  ${({ $showBorder }) => !$showBorder && `
      .ant-tag.ant-tag-volcano {
        color: #F3B765;
        background-color: #442A11;
      }
      .ant-tag.ant-tag-blue {
        color: var(--color-primary);
        background-color: #112545;
      }
      .ant-tag.ant-tag-green {
        background-color: #1D3712;
      }
      .ant-tag.ant-tag-red {
        color: #F37370;
        background-color: #431418;
      }
  `}
`


const SMenuWrapper = styled.div<{ $showBorder: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px;
  border-radius: 8px;


    ${({ $showBorder }) => !$showBorder ? `
      background-color: rgba(20, 20, 20, 1);
      box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);
          .ant-tag.ant-tag-volcano {
        color: #F3B765;
        background-color: #442A11;
      }
      .ant-tag.ant-tag-blue {
        color: var(--color-primary);
        background-color: #112545;
      }
      .ant-tag.ant-tag-green {
        background-color: #1D3712;
      }
      .ant-tag.ant-tag-red {
        color: #F37370;
        background-color: #431418;
      }
      ` : `
      background-color: #FFF;
      box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);

    `}
`

const STag = styled(Tag)`
  color: ${({ color }) => {
    if (color === 'blue') {
      return 'var(--color-primary)'
    }
    return color
  }};
`
